import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Link } from './Link';
import { useAuth } from '../lib/auth';
import { useFeatures } from '../lib/useFeatures';
import { Logo, Wordmark } from './Brand';
import { ThemeToggle } from './ThemeToggle';
import { Button } from './ui';

/**
 * Canonical primary nav shown on EVERY public page so the marketing site has one
 * consistent menu (desktop inline + mobile burger). The link to the page you're
 * currently on is dropped (exact-match) so a page never links to itself — but child
 * routes keep their section link (e.g. a tool detail page still links back to
 * "Free tools"), since only the exact index path is omitted.
 */
const PUBLIC_NAV: ReadonlyArray<{ to: string; label: string }> = [
  { to: '/features', label: 'Features' },
  { to: '/tools', label: 'Free tools' },
  { to: '/pricing', label: 'Pricing' },
  { to: '/partners', label: 'Partners' },
  { to: '/docs', label: 'Docs' },
];

/**
 * Shared header for every public page (Landing / Features / Tools / Pricing / Docs /
 * Legal). Auth-aware: when the user is signed in, the "Sign in" + "Get started" CTAs
 * collapse into a single "Open console →" (or "Open admin →" for super-admins) button —
 * so a logged-in user dropping back onto the marketing site isn't prompted to sign in
 * again or sent to a registration page they already completed.
 *
 * Responsive: the inline nav shows from `md` up; below that (phones in portrait) it
 * collapses into a burger that toggles a stacked dropdown so the row never overflows.
 *
 * Every page gets the same {@link PUBLIC_NAV} automatically. The optional `extraLinks`
 * slot appends page-specific items after the canonical set (deduped by path).
 */
export function PublicHeader({ extraLinks }: { extraLinks?: ReadonlyArray<{ to: string; label: string }> }) {
  const user = useAuth((s) => s.user);
  const { feature } = useFeatures();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const consolePath = user?.role === 'super_admin' ? '/admin' : '/app';
  const consoleLabel = user?.role === 'super_admin' ? 'Open admin →' : 'Open console →';

  // Canonical nav minus the current page's own link, plus any page extras, deduped.
  // Drop the Partners link when the affiliate program is globally disabled.
  //
  // Compare on the trailing-slash-normalized path: the SSG prerender renders this at the bare
  // path (`/pricing`) while the browser hydrates at the served trailing-slash URL (`/pricing/`),
  // so a raw `l.to !== pathname` suppresses the self-link on the server but keeps it on the
  // client — a nav-list divergence that fails hydration (React #418) on every nav-linked page.
  const here = pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname;
  const links = [...PUBLIC_NAV.filter((l) => l.to !== here), ...(extraLinks ?? [])]
    .filter((l) => l.to !== '/partners' || feature('affiliate'))
    .filter((l, i, arr) => arr.findIndex((x) => x.to === l.to) === i);

  const close = () => setOpen(false);

  // Auth CTAs, reused inline (desktop) and stacked (mobile dropdown).
  const ctas = (full: boolean) =>
    user ? (
      <Link to={consolePath} onClick={close} className={full ? 'w-full' : undefined}>
        <Button variant="primary" className={full ? 'w-full' : undefined}>
          {consoleLabel}
        </Button>
      </Link>
    ) : (
      <>
        <Link to="/login" onClick={close} className={full ? 'w-full' : undefined}>
          <Button variant="ghost" className={full ? 'w-full' : undefined}>
            Sign in
          </Button>
        </Link>
        <Link to="/register" onClick={close} className={full ? 'w-full' : undefined}>
          <Button variant="primary" className={full ? 'w-full' : undefined}>
            Get started
          </Button>
        </Link>
      </>
    );

  return (
    <header className="sticky top-0 z-40 -mx-6 border-b border-bordersoft bg-bg/80 px-6 py-4 backdrop-blur-md">
      <div className="flex items-center justify-between gap-3">
        <Link to="/" className="flex items-center gap-3">
          <Logo size={40} />
          <Wordmark className="text-2xl" />
        </Link>

        {/* Desktop / tablet: inline nav */}
        <nav className="hidden items-center gap-2 md:flex">
          {links.map((l) => (
            // `Link` is components/Link — it emits the trailing-slash href. `l.to` stays bare so
            // the self-link filter above (and its React #418 fix) keeps comparing like with like.
            <Link key={l.to} to={l.to}>
              <Button variant="ghost">{l.label}</Button>
            </Link>
          ))}
          {ctas(false)}
          <ThemeToggle compact />
        </nav>

        {/* Mobile: theme toggle + burger */}
        <div className="flex items-center gap-2 md:hidden">
          <ThemeToggle compact />
          <button
            type="button"
            aria-label={open ? 'Close menu' : 'Open menu'}
            aria-expanded={open}
            onClick={() => setOpen((v) => !v)}
            className="inline-flex h-11 w-11 items-center justify-center rounded-[10px] border border-border bg-panel2 text-txt transition hover:bg-hover"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              {open ? (
                <>
                  <line x1="6" y1="6" x2="18" y2="18" />
                  <line x1="6" y1="18" x2="18" y2="6" />
                </>
              ) : (
                <>
                  <line x1="3" y1="6" x2="21" y2="6" />
                  <line x1="3" y1="12" x2="21" y2="12" />
                  <line x1="3" y1="18" x2="21" y2="18" />
                </>
              )}
            </svg>
          </button>
        </div>
      </div>

      {/* Mobile dropdown */}
      {open && (
        <nav className="absolute inset-x-0 top-full z-50 mt-2 flex flex-col gap-2 rounded-[12px] border border-border bg-panel2 p-3 shadow-[0_18px_40px_rgba(0,0,0,.35)] md:hidden">
          {links.map((l) => (
            <Link key={l.to} to={l.to} onClick={close} className="w-full">
              <Button variant="ghost" className="w-full justify-start">
                {l.label}
              </Button>
            </Link>
          ))}
          {ctas(true)}
        </nav>
      )}
    </header>
  );
}
